import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, Search, AlertTriangle, AlertCircle, Eye, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cobrosExpensasApi, expensasApi } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { CobroDetalleDialog } from '../dialogs/CobroDetalleDialog';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  const sign = v < 0 ? '-' : '';
  return `${sign}$ ${Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};
const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  if (d.length !== 3) return s;
  return `${d[2]}/${d[1]}/${d[0]}`;
};
const fmtPeriodo = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};
const hoyMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const addMonths = (mes, delta) => {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const ESTADO_BADGE = {
  parcial: { color: 'bg-amber-50 text-amber-700 ring-amber-200', label: 'Parcial' },
  vencido: { color: 'bg-rose-50 text-rose-700 ring-rose-200', label: 'Vencido' },
  pendiente: { color: 'bg-slate-100 text-slate-700 ring-slate-200', label: 'Pendiente' },
  en_revision: { color: 'bg-violet-50 text-violet-700 ring-violet-200', label: 'En revisión' },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_BADGE[estado] || ESTADO_BADGE.vencido;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${cfg.color}`} data-testid={`badge-moroso-${estado}`}>
      {cfg.label}
    </span>
  );
}

function KpiCard({ label, value, sub, tone = 'slate', testId }) {
  const tones = {
    rose: 'bg-rose-50/50 border-rose-200',
    amber: 'bg-amber-50/50 border-amber-200',
    slate: 'bg-white border-slate-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className="text-base font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function TabMorosos({ edificioId, registerHeader }) {
  const [mes, setMes] = useState(hoyMes());
  const [periodo, setPeriodo] = useState(null);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingData, setLoadingData] = useState(false);

  const [propMap, setPropMap] = useState({});
  const [search, setSearch] = useState('');

  const [detalleDialog, setDetalleDialog] = useState({ open: false, unidadId: null, propNombre: '' });

  // Cargar período del mes
  useEffect(() => {
    let cancelled = false;
    setLoadingPeriodo(true);
    setPeriodo(null);
    (async () => {
      try {
        const r = await expensasApi.getOrCreatePeriodoPorMes(edificioId, mes);
        if (!cancelled) setPeriodo(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Error al obtener período');
      } finally {
        if (!cancelled) setLoadingPeriodo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [edificioId, mes]);

  // Cargar propietarios para resolver nombres
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await expensasApi.listPropietariosEdificio(edificioId);
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach((p) => {
          map[p.propietario_id] = p.nombre_razon_social || p.razon_social || p.nombre || p.email || '—';
        });
        setPropMap(map);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [edificioId]);

  // Cargar morosos
  const loadData = useCallback(async () => {
    if (!periodo?.periodo_id) return;
    setLoadingData(true);
    try {
      const r = await cobrosExpensasApi.morosos(edificioId, periodo.periodo_id);
      setItems(r.data?.items || []);
      setTotal(r.data?.total_saldo_pendiente || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando morosos');
    } finally {
      setLoadingData(false);
    }
  }, [edificioId, periodo?.periodo_id]);

  useEffect(() => {
    if (periodo?.periodo_id) loadData();
  }, [periodo?.periodo_id, loadData]);

  // El selector de mes ahora vive en la fila de filtros (no en el header superior).
  const setHeader = registerHeader;
  useEffect(() => {
    if (!setHeader) return undefined;
    setHeader(null);
    return () => setHeader(null);
  }, [setHeader]);

  // Filtro por buscador
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => {
      const prop = propMap[it.propietario_id] || '';
      return (it.unidad_nombre || '').toLowerCase().includes(s) || prop.toLowerCase().includes(s);
    });
  }, [items, search, propMap]);

  // KPIs
  const kpis = useMemo(() => {
    if (filtered.length === 0) {
      return { totalMoroso: 0, cantidad: 0, mayorDeuda: 0, promedioDias: 0, mayorDeudaUnidad: null };
    }
    const totalMoroso = filtered.reduce((s, it) => s + Number(it.saldo_pendiente || 0), 0);
    const max = filtered.reduce((acc, it) => (Number(it.saldo_pendiente || 0) > Number(acc?.saldo_pendiente || 0) ? it : acc), null);
    const sumDias = filtered.reduce((s, it) => s + Number(it.dias_vencidos || 0), 0);
    return {
      totalMoroso,
      cantidad: filtered.length,
      mayorDeuda: Number(max?.saldo_pendiente || 0),
      mayorDeudaUnidad: max?.unidad_nombre || null,
      promedioDias: Math.round(sumDias / filtered.length),
    };
  }, [filtered]);

  const onOpenDetalle = (m) => setDetalleDialog({
    open: true,
    unidadId: m.unidad_id,
    propNombre: propMap[m.propietario_id] || '—',
  });
  const onCloseDetalle = () => setDetalleDialog({ open: false, unidadId: null, propNombre: '' });

  if (loadingPeriodo || !periodo) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="tab-morosos-content">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard
          label="Total moroso"
          value={fmtMoney(kpis.totalMoroso)}
          sub={`Σ saldo pendiente vencido`}
          tone="rose"
          testId="kpi-total-moroso"
        />
        <KpiCard
          label="Unidades morosas"
          value={kpis.cantidad}
          sub={kpis.cantidad === 1 ? '1 unidad' : `${kpis.cantidad} unidades`}
          tone="rose"
          testId="kpi-cantidad-morosas"
        />
        <KpiCard
          label="Mayor deuda"
          value={fmtMoney(kpis.mayorDeuda)}
          sub={kpis.mayorDeudaUnidad ? kpis.mayorDeudaUnidad : '—'}
          tone="amber"
          testId="kpi-mayor-deuda"
        />
        <KpiCard
          label="Promedio días mora"
          value={kpis.promedioDias > 0 ? `${kpis.promedioDias} días` : '—'}
          sub={`Sobre ${kpis.cantidad} unidades`}
          tone="amber"
          testId="kpi-promedio-dias"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setMes((m) => addMonths(m, -1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Mes anterior" data-testid="btn-mes-prev-morosos">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 bg-white text-xs h-8">
            <Calendar className="w-3.5 h-3.5 text-rose-500" />
            <span className="font-medium text-slate-700 capitalize" data-testid="morosos-periodo-label">{fmtPeriodo(mes)}</span>
          </div>
          <button onClick={() => setMes((m) => addMonths(m, 1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Mes siguiente" data-testid="btn-mes-next-morosos">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            data-testid="input-search-morosos"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por unidad o propietario..."
            className="pl-8 h-8 text-xs"
          />
        </div>
        <div className="text-xs text-slate-500" data-testid="morosos-count">
          {filtered.length} de {items.length}
        </div>
        {loadingData && (
          <div className="ml-auto flex items-center text-xs text-slate-400">
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            Actualizando…
          </div>
        )}
      </div>

      {/* Tabla / Empty states */}
      {loadingData && items.length === 0 ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        periodo?.estado !== 'aprobado' ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="empty-morosos-no-aprobado">
            <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-slate-700">Sin información de morosos para {fmtPeriodo(mes)}</h3>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
              Primero aprobá la liquidación para generar los cobros y poder identificar morosos.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-emerald-200 rounded-xl p-12 text-center" data-testid="empty-morosos-sin-morosos">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2 text-xl">✓</div>
            <h3 className="text-sm font-semibold text-emerald-700">Sin morosos para {fmtPeriodo(mes)}</h3>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
              Ninguna unidad tiene saldo pendiente vencido en este período.
            </p>
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-results-morosos">
          Sin resultados para el filtro actual.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Unidad</th>
                  <th className="px-2 py-2 text-left font-medium">Propietario</th>
                  <th className="px-2 py-2 text-right font-medium">Total a cobrar</th>
                  <th className="px-2 py-2 text-right font-medium">Cobrado</th>
                  <th className="px-2 py-2 text-right font-medium">Saldo pendiente</th>
                  <th className="px-2 py-2 text-center font-medium">Vencimiento</th>
                  <th className="px-2 py-2 text-center font-medium">Días mora</th>
                  <th className="px-2 py-2 text-center font-medium">Último pago</th>
                  <th className="px-2 py-2 text-center font-medium">Estado</th>
                  <th className="px-2 py-2 text-right font-medium w-[80px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const propNombre = propMap[m.propietario_id] || (m.propietario_id ? '—' : 'Sin propietario');
                  const diasMoraSeveridad = m.dias_vencidos > 60
                    ? 'bg-rose-100 text-rose-800'
                    : m.dias_vencidos > 30
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-100 text-slate-700';
                  return (
                    <tr key={m.unidad_id} className="border-b border-slate-100 hover:bg-rose-50/30" data-testid={`moroso-row-${m.unidad_id}`}>
                      <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                        {m.unidad_nombre}
                        <span className="text-slate-400 ml-1 text-[10px]">{m.tipo}</span>
                      </td>
                      <td className="px-2 py-2 text-slate-700 max-w-[180px] truncate" title={propNombre}>{propNombre}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-slate-900">{fmtMoney(m.total_a_cobrar)}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${Number(m.total_cobrado) > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                        {fmtMoney(m.total_cobrado)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-rose-700">
                        {fmtMoney(m.saldo_pendiente)}
                      </td>
                      <td className="px-2 py-2 text-center text-slate-600">{fmtFecha(m.fecha_vto_final)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${diasMoraSeveridad}`}>
                          {m.dias_vencidos}d
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center text-slate-600">
                        {m.ultima_fecha_pago ? fmtFecha(m.ultima_fecha_pago) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <EstadoBadge estado={m.estado} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => onOpenDetalle(m)}
                          className="p-1 rounded hover:bg-indigo-50 text-indigo-600"
                          title="Ver detalle"
                          data-testid={`btn-detalle-moroso-${m.unidad_id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 text-xs font-semibold">
                <tr>
                  <td colSpan={2} className="px-2 py-2 text-slate-600">Totales ({filtered.length})</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(filtered.reduce((s, m) => s + Number(m.total_a_cobrar || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{fmtMoney(filtered.reduce((s, m) => s + Number(m.total_cobrado || 0), 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-rose-700">{fmtMoney(filtered.reduce((s, m) => s + Number(m.saldo_pendiente || 0), 0))}</td>
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Modal detalle (reutilizado de Cobros) */}
      <CobroDetalleDialog
        open={detalleDialog.open}
        onClose={onCloseDetalle}
        edificioId={edificioId}
        periodoId={periodo?.periodo_id}
        unidadId={detalleDialog.unidadId}
        propietarioNombre={detalleDialog.propNombre}
        onChanged={loadData}
      />
    </div>
  );
}
